import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import toast from 'react-hot-toast'

function getPasswordStrength(pw) {
  if (!pw) return 0
  let s = 0
  if (pw.length >= 8)  s++
  if (pw.length >= 12) s++
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++
  if (/[0-9]/.test(pw)) s++
  if (/[^A-Za-z0-9]/.test(pw)) s++
  return Math.min(s, 4)
}

const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong']
const strengthColor = ['', '#ef4444', '#f97316', '#3b82f6', '#22c55e']

export default function RegisterPage() {
  const [fullName, setFullName]       = useState('')
  const [email, setEmail]             = useState('')
  const [password, setPassword]       = useState('')
  const [confirm, setConfirm]         = useState('')
  const [showPw, setShowPw]           = useState(false)
  const [showCf, setShowCf]           = useState(false)
  const [agreed, setAgreed]           = useState(false)
  const [isLoading, setIsLoading]     = useState(false)
  const [errors, setErrors]           = useState({})
  const navigate    = useNavigate()
  const { register } = useAuthStore()

  const strength = getPasswordStrength(password)

  const validate = () => {
    const errs = {}
    if (!fullName.trim()) errs.fullName = 'Full name is required'
    if (!email) errs.email = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = 'Enter a valid email address'
    if (!password) errs.password = 'Password is required'
    else if (password.length < 8) errs.password = 'Password must be at least 8 characters'
    if (password !== confirm) errs.confirm = 'Passwords do not match'
    if (!agreed) errs.agreed = 'You must accept the terms to continue'
    return errs
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setErrors({})
    setIsLoading(true)
    try {
      await register(email, password, fullName.trim())
      toast.success('Account created! Welcome to AcdyON Scholar.')
      navigate('/onboarding', { replace: true })
    } catch (error) {
      toast.error(error.message || 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const inputCls = (hasError) => [
    'w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-all duration-200',
    'bg-white border text-slate-900 placeholder:text-slate-400',
    hasError ? 'border-red-400 focus:border-red-400' : 'border-slate-200 focus:border-blue-400',
    'focus:ring-2 focus:ring-blue-500/20',
  ].join(' ')

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* ── Left panel (dark) 42% ── */}
      <div
        className="hidden lg:flex flex-col w-[42%] relative overflow-hidden"
        style={{ background: '#0f172a' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
        <div
          className="absolute pointer-events-none"
          style={{
            top: '-80px', right: '-80px',
            width: '360px', height: '360px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(37,99,235,0.25) 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 p-8">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
              style={{ background: '#2563eb', color: '#fff' }}>A</div>
            <div className="flex items-baseline gap-1">
              <span className="font-semibold text-sm tracking-wide" style={{ color: '#f1f5f9' }}>AcdyON</span>
              <span className="italic text-sm" style={{ color: '#60a5fa', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Scholar</span>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-12 pb-16">
          <blockquote className="text-center max-w-xs">
            <p style={{
              fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
              fontSize: '1.5rem',
              lineHeight: '1.45',
              color: '#e2e8f0',
              fontStyle: 'italic',
            }}>
              "Your doctoral journey starts here. We'll handle the writing."
            </p>
            <div className="mx-auto mt-7 mb-5" style={{ width: 40, height: 1, background: '#2563eb' }} />
            <cite className="not-italic text-sm" style={{ color: '#64748b' }}>
              — AcdyON Scholar Platform
            </cite>
          </blockquote>

          <div className="mt-12 flex flex-wrap gap-2 justify-center">
            {['SOP Generator', 'Thesis Writer', 'Research Assistant', 'References'].map(f => (
              <span key={f} className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: 'rgba(37,99,235,0.15)', color: '#93c5fd', border: '1px solid rgba(37,99,235,0.25)' }}>
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel (white) 58% ── */}
      <div className="flex-1 flex items-center justify-center px-8 py-12" style={{ background: '#ffffff', overflowY: 'auto' }}>
        <div className="w-full max-w-[420px]">

          <div className="mb-8">
            <h1 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '2rem', color: '#0f172a', fontWeight: 700, lineHeight: 1.2 }}>
              Create account
            </h1>
            <p className="mt-2 text-sm" style={{ color: '#64748b' }}>
              Start your doctoral writing journey today
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Full name */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#475569' }}>Full Name</label>
              <input
                type="text"
                placeholder="Dr. Jane Smith"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                autoComplete="name"
                className={inputCls(!!errors.fullName)}
              />
              {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#475569' }}>Email</label>
              <input
                type="email"
                placeholder="you@university.edu"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                className={inputCls(!!errors.email)}
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>

            {/* Password + strength bar */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#475569' }}>Password</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className={inputCls(!!errors.password) + ' pr-12'}
                />
                <button type="button" tabIndex={-1} onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: '#94a3b8' }}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && (
                <div className="flex items-center gap-1.5 mt-2">
                  {[1, 2, 3, 4].map(seg => (
                    <div key={seg} className="h-1 flex-1 rounded-full transition-colors duration-300"
                      style={{ background: strength >= seg ? strengthColor[strength] : '#e2e8f0' }} />
                  ))}
                  <span className="text-xs font-medium ml-1 shrink-0" style={{ color: strengthColor[strength] }}>
                    {strengthLabel[strength]}
                  </span>
                </div>
              )}
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#475569' }}>Confirm Password</label>
              <div className="relative">
                <input
                  type={showCf ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className={inputCls(!!errors.confirm) + ' pr-12'}
                />
                <button type="button" tabIndex={-1} onClick={() => setShowCf(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: '#94a3b8' }}>
                  {showCf ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirm && <p className="mt-1 text-xs text-red-500">{errors.confirm}</p>}
            </div>

            {/* Terms */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded shrink-0 accent-blue-600"
                />
                <span className="text-xs leading-relaxed" style={{ color: '#64748b' }}>
                  I agree to the{' '}
                  <Link to="/terms" style={{ color: '#2563eb' }}>Terms of Service</Link>{' '}
                  and{' '}
                  <Link to="/privacy" style={{ color: '#2563eb' }}>Privacy Policy</Link>
                </span>
              </label>
              {errors.agreed && <p className="mt-1 text-xs text-red-500 ml-7">{errors.agreed}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
              style={{ background: '#2563eb', color: '#ffffff' }}
              onMouseEnter={e => { if (!isLoading) e.currentTarget.style.background = '#1d4ed8' }}
              onMouseLeave={e => { if (!isLoading) e.currentTarget.style.background = '#2563eb' }}
            >
              {isLoading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm" style={{ color: '#64748b' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-semibold" style={{ color: '#2563eb' }}>Sign in →</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
